import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchJugadorData, fetchJugadorGamelog } from "./useJugadorApi";

import { imageUrl as buildImageUrl } from '../../../config/api';
function buildHighItem(label, source) {
    if (!source || !source.valor) {
        return { label, value: 0, partidos: [] };
    }

    return {
        label,
        value: source.valor,
        partidos: Array.isArray(source.partidos) ? source.partidos : [],
    };
}

function normalizeList(value) {
    return Array.isArray(value) ? value : [];
}

export default function useJugadorData(nombreJugador) {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [data, setData] = useState([]);
    const [maximos, setMaximos] = useState({
        puntos: null,
        asistencias: null,
        rebotes: null,
        robos: null,
        tapas: null,
        dobles: null,
        triples: null,
        libres: null,
    });
    const [ultimosCinco, setUltimosCinco] = useState([]);
    const [positionsRecords, setPositionsRecords] = useState([]);
    const [positionsMaximos, setPositionsMaximos] = useState([]);
    const [isGameLogLoading, setIsGameLogLoading] = useState(false);
    const [selectedLeague, setSelectedLeague] = useState("");
    const [gameLog, setGameLog] = useState([]);
    
    // Track if we've already restored from cache on mount
    const hadCacheAtMountRef = useRef(false);

    useEffect(() => {
        let isMounted = true;
        hadCacheAtMountRef.current = false;

        async function loadJugador() {
            setIsError(false);
            setGameLog([]);
            setSelectedLeague("");

            try {
                const payload = await fetchJugadorData(nombreJugador);
                if (!isMounted) return;

                setData(normalizeList(payload.data));
                setMaximos({
                    puntos: payload.maximos.puntos,
                    asistencias: payload.maximos.asistencias,
                    rebotes: payload.maximos.rebotes,
                    robos: payload.maximos.robos,
                    tapas: payload.maximos.tapas,
                    dobles: payload.maximos.dobles,
                    triples: payload.maximos.triples,
                    libres: payload.maximos.libres,
                });
                setUltimosCinco(normalizeList(payload.ultimosCinco));
                setPositionsRecords(normalizeList(payload.posicionesRecords));
                setPositionsMaximos(normalizeList(payload.posicionesMaximos));
            } catch (error) {
                if (!isMounted) return;
                setIsError(true);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadJugador();

        return () => {
            isMounted = false;
        };
    }, [nombreJugador]);

    const jugadorProfile = useMemo(() => data.find((item) => item?.jugador), [data]);
    const dtProfile = useMemo(() => data.find((item) => !item?.jugador), [data]);
    const principalProfile = jugadorProfile || dtProfile || data[0] || null;
    const hasJugadorProfile = Boolean(jugadorProfile);
    const hasTecnicoProfile = Boolean(dtProfile);

    const imageUrl = useMemo(
        () => buildImageUrl(nombreJugador),
        [nombreJugador]
    );

    const highs = useMemo(
        () => [
            buildHighItem("Puntos", maximos.puntos),
            buildHighItem("Asistencias", maximos.asistencias),
            buildHighItem("Rebotes", maximos.rebotes),
            buildHighItem("Robos", maximos.robos),
            buildHighItem("Tapas", maximos.tapas),
            buildHighItem("Triples", maximos.triples),
            buildHighItem("Dobles", maximos.dobles),
            buildHighItem("Libres", maximos.libres),
        ],
        [maximos]
    );

    const loadGameLog = useCallback(
        async (liga) => {
            if (!liga) {
                setSelectedLeague("");
                setGameLog([]);
                return;
            }

            if (!nombreJugador) {
                return;
            }

            setSelectedLeague(liga);
            setIsGameLogLoading(true);

            try {
                const result = await fetchJugadorGamelog(nombreJugador, liga);
                setGameLog(normalizeList(result));
            } catch (error) {
                setGameLog([]);
            } finally {
                setIsGameLogLoading(false);
            }
        },
        [nombreJugador]
    );

    const gameLogFlags = useMemo(() => {
        const firstStats = gameLog[0]?.jugador?.[0] || {};

        return {
            hasNumero: firstStats.numero !== undefined,
            hasCampos: firstStats.camposHechos !== undefined,
            hasTapasRec: firstStats.tapasRec !== undefined,
            hasFoulsRec: firstStats.foulsRec !== undefined,
            hasMasMenos: firstStats.masMenos !== undefined,
            hasValor: firstStats.valor !== undefined,
        };
    }, [gameLog]);

    return {
        isLoading,
        isError,
        hasData: data.length > 0,
        displayName: principalProfile?.nombre || nombreJugador,
        principalProfile,
        jugadorProfile,
        hasJugadorProfile,
        dtProfile,
        hasTecnicoProfile,
        imageUrl,
        highs,
        averages: normalizeList(jugadorProfile?.torneosProm),
        totals: normalizeList(jugadorProfile?.torneos),
        careerProm: jugadorProfile?.carreraProm || null,
        careerTotals: jugadorProfile?.carrera || null,
        dtTotals: normalizeList(dtProfile?.torneos),
        dtCareerTotals: dtProfile?.carrera || null,
        ultimosCinco,
        positionsRecords,
        positionsMaximos,
        selectedLeague,
        gameLog,
        gameLogFlags,
        isGameLogLoading,
        loadGameLog,
    };
}
